import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Lightning, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

function trialStatus(user) {
  if (!user) return { state: "anon" };
  if (user.role === "admin") return { state: "admin" };
  const until = user.trial_until;
  if (!until) return { state: "no_trial" };
  try {
    const dt = new Date(until);
    if (dt > new Date()) return { state: "active", until: dt };
  } catch { /* fall through */ }
  return { state: "expired" };
}

const fmtUntil = (dt) => {
  if (!dt) return "—";
  const days = Math.max(0, Math.ceil((dt - new Date()) / (24 * 3600 * 1000)));
  return `${days} ${days === 1 ? "day" : "days"} left`;
};

export default function Subscribe() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [activating, setActivating] = useState(false);
  const [status, setStatus] = useState(() => trialStatus(user));

  useEffect(() => { setStatus(trialStatus(user)); }, [user]);

  const activateTrial = async () => {
    setActivating(true);
    try {
      const { data } = await api.post("/trial/activate", {});
      toast.success("Free trial activated. Welcome aboard.");
      if (typeof refresh === "function") await refresh();
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail || "Activation failed";
      toast.error(detail);
    } finally {
      setActivating(false);
    }
  };

  // Already-admin pass-through
  if (status.state === "admin") {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-16 text-center" data-testid="subscribe-admin-passthrough">
        <h1 className="font-display text-5xl mb-4">YOU'RE AN ADMIN</h1>
        <p className="text-zinc-400 mb-6">You already have full access. Subscriptions don't apply.</p>
        <Link to="/sports/mlb" className="text-[#007AFF] hover:underline">Back to picks →</Link>
      </div>
    );
  }
  // Already in active trial
  if (status.state === "active") {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-16 text-center" data-testid="subscribe-already-active">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#22C55E] mb-2">Trial Active</div>
        <h1 className="font-display text-5xl mb-4">YOU'RE IN.</h1>
        <p className="text-zinc-300 text-lg mb-2">Your free trial has <strong>{fmtUntil(status.until)}</strong>.</p>
        <p className="text-zinc-500 mb-8">We'll email you a day before it ends with a one-click subscribe link.</p>
        <Link to="/sports/mlb" className="inline-block bg-[#007AFF] hover:bg-[#0064D6] text-white px-6 py-3 rounded-sm text-xs uppercase tracking-[0.25em] font-bold transition-colors">
          See today's picks →
        </Link>
      </div>
    );
  }

  const isAnon = status.state === "anon";
  const trialUsed = status.state === "expired";

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-12" data-testid="subscribe-page">
      <div className="mb-10 text-center">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-2">Choose your access</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none">UNLOCK THE PICKS.</h1>
        <p className="text-sm text-zinc-400 mt-4 max-w-2xl mx-auto leading-relaxed">
          Charlie hunts bounce-back patterns. Dakota rides momentum continuation.
          Start free for a week, or jump straight to a weekly subscription.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Free Trial Card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className={`relative bg-[#121212] border ${trialUsed ? "border-white/10 opacity-60" : "border-[#22C55E]/40"} rounded-sm p-8`}
          data-testid="plan-trial"
        >
          {!trialUsed && (
            <div className="absolute -top-3 left-6 bg-[#22C55E] text-black text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-sm">
              Start here · No card
            </div>
          )}
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#22C55E]">7-Day Free Trial</div>
            <Lightning size={20} weight="fill" className="text-[#22C55E]" />
          </div>
          <div className="font-display text-5xl mb-1">FREE</div>
          <div className="text-xs text-zinc-500 uppercase tracking-widest mb-6">For 7 days · Then cancel or subscribe</div>

          <ul className="space-y-2.5 mb-8 text-sm text-zinc-300">
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#22C55E] flex-shrink-0 mt-0.5" /> Every daily pick from Charlie & Dakota</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#22C55E] flex-shrink-0 mt-0.5" /> Live DraftKings moneylines + stake amounts</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#22C55E] flex-shrink-0 mt-0.5" /> Tamper-evident locked-pick receipts</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#22C55E] flex-shrink-0 mt-0.5" /> Email reminder before trial ends</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#22C55E] flex-shrink-0 mt-0.5" /> No credit card required</li>
          </ul>

          {trialUsed ? (
            <div className="text-center text-xs uppercase tracking-widest text-zinc-500 py-3 border border-white/10 rounded-sm" data-testid="trial-used-notice">
              You&apos;ve already used your free trial
            </div>
          ) : isAnon ? (
            <Link
              to="/signup"
              data-testid="trial-cta-register"
              className="block w-full bg-[#22C55E] hover:bg-[#16A34A] text-black text-center font-bold uppercase tracking-[0.2em] py-3.5 rounded-sm text-xs transition-colors"
            >
              Create account → Start trial
            </Link>
          ) : (
            <button
              onClick={activateTrial}
              disabled={activating}
              data-testid="trial-activate-btn"
              className="w-full bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-50 text-black font-bold uppercase tracking-[0.2em] py-3.5 rounded-sm text-xs transition-colors"
            >
              {activating ? "Activating…" : "Activate 7-day free trial"}
            </button>
          )}
        </motion.div>

        {/* Subscription Card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.05 } }}
          className="relative bg-[#121212] border border-[#007AFF]/40 rounded-sm p-8"
          data-testid="plan-subscription"
        >
          <div className="absolute -top-3 left-6 bg-[#007AFF] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-sm">
            Best value
          </div>
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF]">Weekly Subscription</div>
            <CreditCard size={20} weight="fill" className="text-[#007AFF]" />
          </div>
          <div className="flex items-baseline gap-1.5 mb-1">
            <div className="font-display text-5xl">$15</div>
            <div className="text-zinc-500 text-sm">/ week</div>
          </div>
          <div className="text-xs text-zinc-500 uppercase tracking-widest mb-6">Cancel anytime · Auto-renews each week</div>

          <ul className="space-y-2.5 mb-8 text-sm text-zinc-300">
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#007AFF] flex-shrink-0 mt-0.5" /> Everything in the free trial</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#007AFF] flex-shrink-0 mt-0.5" /> Full strategy history & per-team breakdown</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#007AFF] flex-shrink-0 mt-0.5" /> Priority email support</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#007AFF] flex-shrink-0 mt-0.5" /> Cancel from your account anytime</li>
          </ul>

          <Link
            to="/signup"
            data-testid="subscribe-cta-stripe"
            className="block w-full bg-[#007AFF] hover:bg-[#0064D6] text-white text-center font-bold uppercase tracking-[0.2em] py-3.5 rounded-sm text-xs transition-colors"
          >
            Subscribe Now →
          </Link>
          <div className="text-[10px] text-center text-zinc-600 mt-2 uppercase tracking-widest">
            Includes the 7-day free trial · Card on file · Cancel anytime
          </div>
        </motion.div>
      </div>

      <div className="mt-10 text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600">
        21+ · Not financial advice · Gamble responsibly · 1-800-GAMBLER
      </div>
    </div>
  );
}
